import React, { useState } from 'react';
import { Table, Button, Tag, Tooltip } from 'antd';
import {EyeOutlined, QrcodeOutlined, PrinterOutlined, CameraOutlined} from '@ant-design/icons';
import { getColorFromText } from '../../utils/getColorFromText';
import { useAuth } from '../../context/AuthContext';

const ParticipantsTable = ({ data, onQRCodeClick, onPrintBadgeClick, onShowDetails, showCamera }) => {
    const { hasFeature } = useAuth();
    const [openTooltipDetails, setOpenTooltipDetails] = useState(null);
    const [openTooltipQR, setOpenTooltipQR] = useState(null);
    const [openTooltipPrint, setOpenTooltipPrint] = useState(null);
    const [openTooltipCamera, setOpenTooltipCamera] = useState(null);

    const getUniqueValues = (key) => {
        return [...new Set(data.map(item => item[key]).filter(Boolean))].map(value => ({
            text: value,
            value: value
        }));
    };

    const columns = [
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <div style={{ display: 'flex', gap: 8 }}>
                    {hasFeature('participants.details') && (
                        <Tooltip
                            title="View participant details"
                            color="#1677ff"
                            open={openTooltipDetails === record.key}
                            onOpenChange={(open) => {
                                setOpenTooltipDetails(open ? record.key : null);
                            }}
                        >
                            <Button
                                type="default"
                                icon={<EyeOutlined />}
                                onClick={() => {
                                    onShowDetails(record);
                                    setOpenTooltipDetails(null);
                                }}
                                style={{ color: '#1677ff' }}
                            />
                        </Tooltip>
                    )}

                    {hasFeature('participants.qr') && (
                        <Tooltip
                            title="Show QR code"
                            color="#00b96b"
                            open={openTooltipQR === record.key}
                            onOpenChange={(open) => {
                                setOpenTooltipQR(open ? record.key : null);
                            }}
                        >
                            <Button
                                type="default"
                                icon={<QrcodeOutlined />}
                                onClick={() => {
                                    onQRCodeClick(record);
                                    setOpenTooltipQR(null);
                                }}
                                style={{ color: '#00b96b' }}
                            />
                        </Tooltip>
                    )}

                    {hasFeature('participants.print') && (
                        <Tooltip
                            title="Print badge"
                            color="#f5222d"
                            open={openTooltipPrint === record.key}
                            onOpenChange={(open) => {
                                setOpenTooltipPrint(open ? record.key : null);
                            }}
                        >
                            <Button
                                type="default"
                                icon={<PrinterOutlined />}
                                onClick={() => {
                                    onPrintBadgeClick(record);
                                    setOpenTooltipPrint(null);
                                }}
                                style={{ color: '#f5222d' }}
                            />
                        </Tooltip>
                    )}

                    {hasFeature('participants.camera') && (
                        <Tooltip
                            title="Upload photo"
                            color="gray"
                            open={openTooltipCamera === record.key}
                            onOpenChange={(open) => {
                                setOpenTooltipCamera(open ? record.key : null);
                            }}
                        >
                            <Button
                                icon={<CameraOutlined />}
                                type="default"
                                style={{ marginBottom: '20px' }}
                                onClick={() => {
                                    showCamera(record);
                                    setOpenTooltipCamera(null);
                                }}
                            />
                        </Tooltip>
                    )}
                </div>
            )
        },
        {
            title: 'First Name',
            dataIndex: 'firstName',
            key: 'firstName',
            sorter: (a, b) => a.firstName.localeCompare(b.firstName),
            filters: getUniqueValues('firstName'),
            onFilter: (value, record) => record.firstName.includes(value),
            filterSearch: true
        },
        {
            title: 'Last Name',
            dataIndex: 'lastName',
            key: 'lastName',
            sorter: (a, b) => a.lastName.localeCompare(b.lastName),
            filters: getUniqueValues('lastName'),
            onFilter: (value, record) => record.lastName.includes(value),
            filterSearch: true
        },
        {
            title: 'Participant Type',
            dataIndex: 'participantType',
            key: 'participantType',
            sorter: (a, b) => a.participantType.localeCompare(b.participantType),
            filters: getUniqueValues('participantType'),
            onFilter: (value, record) => record.participantType === value,
            filterSearch: true,
            render: (text) => (
                <Tag color={getColorFromText(text)}>{text}</Tag>
            )
        },
        {
            title: 'Participant State',
            dataIndex: 'participantState',
            key: 'participantState',
            sorter: (a, b) => a.participantState.localeCompare(b.participantState),
            filters: getUniqueValues('participantState'),
            onFilter: (value, record) => record.participantState === value,
            filterSearch: true,
            render: (text) => (
                <Tag color={getColorFromText(text)}>{text}</Tag>
            )
        }
    ];

    return <Table columns={columns} dataSource={data} rowKey="key" scroll={{ x: 'max-content' }} />;
};

export default ParticipantsTable;